import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { CourierOptions, CourierProvider, OrderService } from '../../services/order/orderService';

const labelSx = {
  fontSize: 'var(--text-xs)',
  color: 'var(--color-text-muted)',
  mb: '6px',
  fontWeight: 700,
  letterSpacing: 'var(--tracking-wide)',
  textTransform: 'uppercase',
};

export const formatMoney = (amount: number, currency: string, locale: string): string =>
  `${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

const OptionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  detail: React.ReactNode;
  /** Ile restauracji zostaje z dostawy; null - opcja niedostepna, bez kwoty. */
  income: number | null;
  money: (amount: number) => string;
  recommended?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ icon, title, detail, income, money, recommended, disabled, onClick }) => {
  const { t } = useTranslation();
  const negative = income != null && income < 0;

  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        p: 'var(--space-4)',
        border: '1px solid',
        borderColor: recommended ? 'var(--color-primary)' : 'var(--color-border)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--color-surface)',
        transition:
          'border-color var(--dur-fast) var(--ease-standard), background-color var(--dur-fast) var(--ease-standard)',
        '&:hover': disabled
          ? undefined
          : { borderColor: 'var(--color-primary)', backgroundColor: 'var(--color-primary-soft)' },
        '&:focus-visible': { boxShadow: 'var(--shadow-focus)' },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-primary)',
          backgroundColor: 'var(--color-primary-soft)',
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" sx={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 'var(--text-base)' }}>{title}</Typography>
          {recommended && (
            <Chip
              label={t('orders.courier.recommended')}
              size="small"
              sx={{
                height: 20,
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                color: 'var(--color-primary)',
                backgroundColor: 'var(--color-primary-soft)',
              }}
            />
          )}
        </Stack>
        <Typography
          sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', mt: '2px' }}
        >
          {detail}
        </Typography>
      </Box>

      {income != null && (
        <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {t(negative ? 'orders.courier.youPay' : 'orders.courier.youKeep')}
          </Typography>
          <Typography
            sx={{
              fontSize: 'var(--text-base)',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              color: negative ? 'var(--color-warning)' : 'var(--color-text-primary)',
            }}
          >
            {negative ? money(-income) : income > 0 ? `+${money(income)}` : money(0)}
          </Typography>
        </Box>
      )}

      {!disabled && (
        <ChevronRightIcon sx={{ flexShrink: 0, color: 'var(--color-text-muted)', ml: '-4px' }} />
      )}
    </Box>
  );
};

/**
 * Pierwszy krok akceptacji zamowienia z dostawa w lokalu z wlasnymi kurierami: kto wiezie.
 * Przy kazdej opcji pokazuje, ile z dostawy zostanie restauracji, i wyroznia korzystniejsza.
 */
export const CourierChoiceDialog: React.FC<{
  open: boolean;
  orderId: number;
  onClose: () => void;
  onSelect: (courier: CourierProvider) => void;
}> = ({ open, orderId, onClose, onSelect }) => {
  const { t, i18n } = useTranslation();
  const [options, setOptions] = React.useState<CourierOptions | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;

    let active = true;
    setOptions(null);
    setFailed(false);

    new OrderService()
      .getCourierOptions(orderId)
      .then((result) => active && setOptions(result))
      .catch((e) => {
        Sentry.captureException(e);
        if (active) setFailed(true);
      });

    return () => {
      active = false;
    };
  }, [open, orderId]);

  const money = (amount: number) => formatMoney(amount, options?.currency ?? '', i18n.language);
  const km = (value: number) => value.toLocaleString(i18n.language);

  const select = (courier: CourierProvider) => {
    onSelect(courier);
    onClose();
  };

  const own = options?.ownCourier;
  const wolt = options?.wolt;
  const ownIncome = own?.available && own.cost != null ? own.restaurantDeliveryIncome : null;
  const woltIncome =
    wolt?.available && wolt.cost != null ? (wolt.restaurantDeliveryIncome ?? 0) : null;
  // Wyrozniamy tylko, gdy obie opcje sa dostepne i jedna faktycznie zostawia wiecej.
  const recommended: CourierProvider | null =
    ownIncome != null && woltIncome != null && ownIncome !== woltIncome
      ? ownIncome > woltIncome
        ? 'RESTAURANT'
        : 'WOLT'
      : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
        paper: { sx: { borderRadius: 'var(--radius-lg)' } },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          fontWeight: 700,
          pb: 'var(--space-2)',
        }}
      >
        {t('orders.courier.choose')}
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          size="small"
          sx={{ mr: '-8px' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!options && !failed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 'var(--space-6)' }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {failed && (
          <Stack spacing="var(--space-3)">
            <Alert severity="error">{t('orders.courier.loadFailed')}</Alert>
            <Button variant="outlined" onClick={() => select('WOLT')}>
              {t('orders.courier.continueWithWolt')}
            </Button>
          </Stack>
        )}

        {options && wolt && (
          <Stack spacing="var(--space-3)">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{
                gap: 'var(--space-3)',
                px: 'var(--space-3)',
                py: 'var(--space-2)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--color-bg-subtle)',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 'var(--text-sm)' }}>
                  {t('orders.courier.customerPaid')}
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  {t(
                    // Poza ostatnim progiem klient placi cene Wolta, nawet przy wlasnym cenniku.
                    options.deliveryPricing === 'OWN_COURIER' && own?.available
                      ? 'orders.courier.pricedByOwn'
                      : 'orders.courier.pricedByWolt',
                  )}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 'var(--text-sm)', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {money(options.customerDeliveryFee)}
              </Typography>
            </Stack>

            {own && (
              <OptionCard
                icon={<StorefrontOutlinedIcon fontSize="small" />}
                title={t('orders.courier.restaurant')}
                detail={
                  ownIncome != null && own.cost != null
                    ? t('orders.courier.ownDetail', {
                        amount: money(own.cost),
                        km: km(own.distanceKm),
                      })
                    : t('orders.courier.ownOutOfRange', {
                        km: km(own.distanceKm),
                        max: km(own.maxDistanceKm),
                      })
                }
                income={ownIncome}
                money={money}
                recommended={recommended === 'RESTAURANT'}
                disabled={!own.available}
                onClick={() => select('RESTAURANT')}
              />
            )}

            <OptionCard
              icon={<DeliveryDiningOutlinedIcon fontSize="small" />}
              title={t('orders.courier.wolt')}
              detail={
                woltIncome != null && wolt.cost != null
                  ? wolt.estimatedMinutes != null
                    ? t('orders.courier.woltDetailEta', {
                        amount: money(wolt.cost),
                        minutes: wolt.estimatedMinutes,
                      })
                    : t('orders.courier.woltDetail', { amount: money(wolt.cost) })
                  : t('orders.courier.woltUnavailable')
              }
              income={woltIncome}
              money={money}
              recommended={recommended === 'WOLT'}
              disabled={!wolt.available}
              onClick={() => select('WOLT')}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Dostawa kurierem restauracji - w miejscu sledzenia Wolta. */
export const OwnCourierInfo: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ px: 'var(--space-4)', py: 'var(--space-3)' }}>
      <Typography sx={labelSx}>{t('orders.courier.ownDeliveryTitle')}</Typography>
      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        {status === 'ON_THE_WAY'
          ? t('orders.courier.ownOnTheWayHint')
          : t('orders.courier.ownInPreparationHint')}
      </Typography>
    </Box>
  );
};

const primaryButtonSx = {
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-xs)',
  '&:hover': {
    backgroundColor: 'var(--color-primary-hover)',
    boxShadow: 'var(--shadow-md)',
  },
};

/** Przyciski statusu dla zamowienia wiezionego przez kuriera restauracji. */
export const OwnCourierActions: React.FC<{
  orderId: number;
  status: string;
  onError: (e: any) => void;
}> = ({ orderId, status, onError }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);

  const run = async (action: (id: number) => Promise<void>) => {
    setBusy(true);
    try {
      await action(orderId);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const orderService = new OrderService();

  return (
    <>
      {status === 'IN_PREPARATION' && (
        <Button
          variant="outlined"
          disabled={busy}
          onClick={() => run(orderService.markOwnCourierOnTheWay)}
          sx={{ borderRadius: 'var(--radius-md)' }}
        >
          {t('orders.courier.handedToCourier')}
        </Button>
      )}
      {(status === 'IN_PREPARATION' || status === 'ON_THE_WAY') && (
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => run(orderService.markOwnCourierDelivered)}
          sx={primaryButtonSx}
        >
          {t('orders.courier.delivered')}
        </Button>
      )}
    </>
  );
};
